import { BusinessProfile, Entity, UserAccount, UserPreferences } from '@iot-platform/models/common';
import { createFeature, createReducer, on } from '@ngrx/store';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Privileges } from '../../authorization.types';
import { AuthApiActions, AuthBusinessProfilesApiActions, AuthPageActions } from '../actions';

/** ISO expiry date read from the JWT `exp` claim, `''` (= expired) when absent or undecodable. */
const getTokenExpiresAt = (token: string): string => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    return exp ? new Date(exp * 1000).toISOString() : '';
  } catch {
    return '';
  }
};

export interface AuthApiFeatureState {
  loggedIn: boolean;
  loggedOut: boolean;
  tokenExpired: boolean;
  businessProfileInitialized: boolean;
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  currentUser: UserAccount | null;
  userId: string | null;
  firstLogIn: boolean;
  businessProfiles: BusinessProfile[] | null;
  selectedBusinessProfile: BusinessProfile | null;
  currentEntity: Entity | null;
  currentEntityId: string | null;
  perimeterEntities: Entity[] | null;
  privileges: Privileges | null;
  preferences: UserPreferences | null;
  isUserAdmin: boolean;
  loggedInWithSSO: boolean;
  ssoTokenExpiresAt: string;
}

export const initialState: AuthApiFeatureState = {
  loggedIn: false,
  businessProfileInitialized: false,
  loggedOut: false,
  tokenExpired: true,
  idToken: null,
  accessToken: null,
  refreshToken: null,
  currentUser: null,
  userId: null,
  firstLogIn: false,
  businessProfiles: null,
  selectedBusinessProfile: null,
  currentEntity: null,
  currentEntityId: null,
  perimeterEntities: null,
  privileges: null,
  preferences: null,
  isUserAdmin: false,
  loggedInWithSSO: false,
  ssoTokenExpiresAt: ''
};

export const authApiFeature = createFeature({
  name: 'authApi',
  reducer: createReducer(
    initialState,
    on(AuthPageActions.signIn, (): AuthApiFeatureState => initialState),

    on(AuthApiActions.signOutSuccess, (state: AuthApiFeatureState): AuthApiFeatureState => ({
      ...state,
      loggedOut: true
    })),
    on(
      AuthApiActions.signInWithSSOSuccess,
      AuthApiActions.retrieveSsoSessionSuccess,
      (state: AuthApiFeatureState, { idToken, accessToken, refreshToken }): AuthApiFeatureState => ({
        ...state,
        loggedIn: true,
        loggedInWithSSO: true,
        accessToken,
        idToken,
        refreshToken
      })
    ),
    on(AuthApiActions.refreshSsoTokensSuccess, (state: AuthApiFeatureState, { idToken, accessToken }): AuthApiFeatureState => ({
      ...state,
      accessToken,
      idToken
    })),
    // On page reload the SSO tokens come back from storage without `expiresIn`: restore the expiry from the id token
    on(AuthApiActions.retrieveSsoSessionSuccess, (state: AuthApiFeatureState, { idToken }): AuthApiFeatureState => ({
      ...state,
      ssoTokenExpiresAt: getTokenExpiresAt(idToken)
    })),
    on(AuthApiActions.signInSuccess, AuthApiActions.retrieveSessionSuccess, (state: AuthApiFeatureState, { session }): AuthApiFeatureState => ({
      ...state,
      loggedIn: true,
      tokenExpired: false,
      idToken: session.tokens.idToken,
      accessToken: session.tokens.accessToken,
      refreshToken: session.tokens.refreshToken,
      userId: session.user.username
    })),
    on(AuthApiActions.retrieveSessionFailure, (state: AuthApiFeatureState) => {
      localStorage.clear();
      const s = {
        ...state,
        loggedIn: false,
        loggedOut: true,
        tokenExpired: true,
        idToken: null,
        accessToken: null,
        refreshToken: null,
        currentUser: null,
        firstLogIn: false,
        businessProfiles: null,
        selectedBusinessProfile: null,
        currentEntity: null,
        perimeterEntities: null,
        privileges: null,
        preferences: null,
        isUserAdmin: false
      };

      return s;
    }),
    on(AuthApiActions.loadAccountSuccess, (state: AuthApiFeatureState, { account }) => ({
      ...state,
      currentUser: account,
      userId: account.id,
      businessProfiles: account.businessProfiles,
      preferences: account.preferences
    })),
    on(AuthApiActions.setBusinessProfiles, (state: AuthApiFeatureState, { businessProfiles }) => ({
      ...state,
      businessProfiles: [...businessProfiles],
      businessProfileInitialized: true
    })),
    on(AuthApiActions.loadPrivilegesSuccess, (state: AuthApiFeatureState, { privileges }) => ({
      ...state,
      privileges
    })),
    on(AuthPageActions.loadPrivileges, (state: AuthApiFeatureState) => ({
      ...state,
      privileges: null
    })),
    on(AuthApiActions.signInFailure, (state: AuthApiFeatureState, { error }) => ({ ...state, error })),
    on(AuthPageActions.signOut, () => ({ ...initialState })),
    on(AuthApiActions.signOutFailure, (state: AuthApiFeatureState) => ({ ...state })),
    on(AuthApiActions.changePasswordSuccess, (state: AuthApiFeatureState) => ({ ...state, loggedIn: false })),
    on(AuthPageActions.requireNewPassword, (state: AuthApiFeatureState) => ({ ...state, loggedIn: false })),
    on(AuthApiActions.changePasswordFailure, (state: AuthApiFeatureState) => ({ ...state })),
    on(AuthApiActions.forgotPasswordSuccess, (state: AuthApiFeatureState) => ({ ...state })),
    on(AuthApiActions.forgotPasswordFailure, (state: AuthApiFeatureState) => ({ ...state })),
    on(AuthApiActions.checkIfUserIsAdminSuccess, (state: AuthApiFeatureState, { isAdminUser }) => ({
      ...state,
      isUserAdmin: isAdminUser
    })),
    on(AuthBusinessProfilesApiActions.selectBusinessProfileSuccess, (state: AuthApiFeatureState, { selectedBusinessProfile }) => ({
      ...state,
      selectedBusinessProfile
    })),
    on(AuthApiActions.refreshTokenSuccess, (state: AuthApiFeatureState, { session }) => ({
      ...state,
      idToken: session.tokens.idToken,
      accessToken: session.tokens.accessToken,
      refreshToken: session.tokens.refreshToken
    })),
    on(AuthApiActions.refreshSsoTokensSuccess, AuthApiActions.signInWithSSOSuccess, (state: AuthApiFeatureState, { expiresIn }) => ({
      ...state,
      ssoTokenExpiresAt: new Date(Date.now() + Number.parseInt(`${expiresIn}`, 10) * 1000).toISOString()
    })),
    on(AuthBusinessProfilesApiActions.loadSelectedEntityForSessionSuccess, (state: AuthApiFeatureState, { selectedEntity }) => ({
      ...state,
      currentEntity: selectedEntity,
      currentEntityId: selectedEntity.id
    })),
    on(AuthBusinessProfilesApiActions.loadPerimeterEntitiesSuccess, (state: AuthApiFeatureState, { entities }) => ({
      ...state,
      perimeterEntities: entities
    }))
  )
});
